import Head from 'next/head';
import React, { useMemo } from 'react';

/**
 * Supported third-party origins for preconnect hints.
 * @see PreconnectHints.md for full documentation
 */
export type PreconnectOrigin =
  | 'mux-stream'
  | 'mux-image'
  | 'youtube'
  | 'spotify-api'
  | 'spotify-analytics';

const ORIGIN_URLS: Record<PreconnectOrigin, string> = {
  'mux-stream': 'https://stream.mux.com',
  'mux-image': 'https://image.mux.com',
  youtube: 'https://i.ytimg.com', // YouTube thumbnail images
  'spotify-api': 'https://open.scdn.co',
  'spotify-analytics': 'https://ccra.scdn.co',
};

/**
 * Props for the PreconnectHints component
 */
interface PreconnectHintsProps {
  /**
   * Optional: Manually specify which origins to preconnect to.
   * If not provided, origins are auto-detected from pageContent.
   *
   * @example
   * ```tsx
   * <PreconnectHints origins={['mux-stream', 'youtube']} />
   * ```
   */
  origins?: PreconnectOrigin[];

  /**
   * Optional: Page content to auto-detect needed origins from.
   * Pass the template object or modules array.
   * The component will analyze the content to determine which third-party
   * origins are used above the fold.
   *
   * @example
   * ```tsx
   * <PreconnectHints pageContent={template} />
   * ```
   */
  pageContent?: any;

  /**
   * Only check the first N modules (for above-the-fold content).
   * This prevents preconnecting to origins that are only used in
   * lazy-loaded content below the fold.
   *
   * @default 3
   * @example
   * ```tsx
   * <PreconnectHints pageContent={template} aboveFoldCount={5} />
   * ```
   */
  aboveFoldCount?: number;
}

/**
 * Auto-detects which origins are needed based on page content.
 * Only checks above-the-fold modules to avoid preconnecting for lazy-loaded content.
 *
 * Detection logic:
 * - Extracts heroModule + first N modules (default: 3)
 * - Searches for keywords: "mux", "youtube", "youtu.be"
 * - Returns appropriate PreconnectOrigin types
 *
 * @param pageContent - Template or page data to analyze
 * @param aboveFoldCount - Number of modules to check (default: 3)
 * @returns Array of origin types that should be preconnected
 *
 * @internal
 */
function detectNeededOrigins(
  pageContent: any,
  aboveFoldCount: number = 3,
): PreconnectOrigin[] {
  if (!pageContent) return [];

  const origins = new Set<PreconnectOrigin>();

  // For landing pages, only check above-the-fold modules
  let contentToCheck = pageContent;
  if (pageContent.modules && Array.isArray(pageContent.modules)) {
    // Include heroModule (always above fold) + first N modules
    const aboveFoldModules = [
      pageContent.heroModule,
      ...pageContent.modules.slice(0, aboveFoldCount),
    ].filter(Boolean);
    contentToCheck = aboveFoldModules;
  }

  // Convert to lowercase string for searching
  const contentStr = JSON.stringify(contentToCheck).toLowerCase();

  // Check for Mux videos - look for "mux" anywhere
  if (contentStr.includes('mux')) {
    origins.add('mux-stream');
    origins.add('mux-image');
  }

  // Check for YouTube videos - look for youtube URLs
  if (contentStr.includes('youtube') || contentStr.includes('youtu.be')) {
    origins.add('youtube');
  }

  return Array.from(origins);
}

/**
 * Automatically adds `<link rel="preconnect">` hints to improve page load performance
 * by establishing early connections to third-party origins.
 *
 * ## Why use this?
 * - Reduces latency by establishing connections (DNS, TCP, TLS) early
 * - Only preconnects for above-the-fold content (no wasted connections)
 * - Avoids Lighthouse warnings for "unused preconnects"
 * - Zero configuration - automatically detects what's needed
 *
 * ## How it works:
 * 1. Analyzes the first 3 modules (hero + 2 modules) of the page
 * 2. Detects if Mux videos, YouTube embeds, etc. are present
 * 3. Adds appropriate `<link rel="preconnect">` tags to `<head>`
 *
 * @example
 * ```tsx
 * // Automatic detection (recommended)
 * <PreconnectHints pageContent={template} />
 *
 * // Manual override
 * <PreconnectHints origins={['mux-stream', 'youtube']} />
 *
 * // Custom above-fold count
 * <PreconnectHints pageContent={template} aboveFoldCount={5} />
 * ```
 *
 * @see PreconnectHints.md for full documentation
 */
export const PreconnectHints: React.FC<PreconnectHintsProps> = ({
  origins: manualOrigins,
  pageContent,
  aboveFoldCount = 3,
}) => {
  const detectedOrigins = useMemo(
    () => detectNeededOrigins(pageContent, aboveFoldCount),
    [pageContent, aboveFoldCount],
  );

  const origins = manualOrigins || detectedOrigins;

  if (origins.length === 0) return null;

  return (
    <Head>
      {origins.map(origin => (
        <link key={origin} rel="preconnect" href={ORIGIN_URLS[origin]} />
      ))}
    </Head>
  );
};
